import {
  Component,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
  signal,
  computed,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faTimes, faPlus } from "@fortawesome/free-solid-svg-icons";

export interface TagOption {
  value: string;
  label: string;
}

const noop = () => {};

export const TAG_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TagSelectorComponent),
  multi: true,
};

/**
 * Reusable tag selector component with Bootstrap badges.
 * Supports both direct usage with ngModel and Formly integration.
 *
 * Usage:
 * ```html
 * <app-tag-selector
 *   [options]="targetGroupOptions"
 *   [(ngModel)]="entity.targetGroups"
 * ></app-tag-selector>
 * ```
 */
@Component({
  selector: "app-tag-selector",
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FontAwesomeModule],
  providers: [TAG_SELECTOR_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tag-selector d-flex flex-wrap align-items-center gap-2">
      @for (option of options; track option.value) {
        @if (isSelected(option.value)) {
          <!-- Selected badge (primary with remove button) -->
          <span
            class="badge badge-primary d-inline-flex align-items-center py-2 px-3 cursor-pointer"
            (click)="toggleOption(option.value)"
          >
            <span class="mr-2">{{ option.label | translate }}</span>
            <fa-icon [icon]="faTimes" class="small"></fa-icon>
          </span>
        } @else {
          <!-- Unselected badge (light with plus icon) -->
          <span
            class="badge badge-light border d-inline-flex align-items-center py-2 px-3 cursor-pointer tag-unselected"
            (click)="toggleOption(option.value)"
          >
            <fa-icon [icon]="faPlus" class="small mr-2 text-muted"></fa-icon>
            <span>{{ option.label | translate }}</span>
          </span>
        }
      }

      @if (options.length === 0) {
        <span class="text-muted small">{{ 'No options available' | translate }}</span>
      }
    </div>
  `,
  styles: [
    `
      .tag-selector {
        gap: 0.5rem;
      }

      .cursor-pointer {
        cursor: pointer;
      }

      .badge {
        font-weight: 500;
        font-size: 0.875rem;
        transition: all 0.15s ease-in-out;
      }

      .badge-primary {
        background-color: var(--primary, #007bff);
      }

      .badge-primary:hover {
        opacity: 0.85;
      }

      .tag-unselected {
        background-color: #f8f9fa;
        color: #495057;
      }

      .tag-unselected:hover {
        background-color: #e9ecef;
        border-color: #adb5bd !important;
      }
    `,
  ],
})
export class TagSelectorComponent implements ControlValueAccessor {
  @Input() options: TagOption[] = [];
  @Input() placeholder = "";

  readonly faTimes = faTimes;
  readonly faPlus = faPlus;

  // Internal state using signals
  private selectedValues = signal<string[]>([]);

  // Computed for checking selection status
  isSelected(value: string): boolean {
    return this.selectedValues().includes(value);
  }

  // ControlValueAccessor callbacks
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (value: string[]) => void = noop;

  // Toggle an option's selection
  toggleOption(value: string): void {
    const current = this.selectedValues();
    let newValues: string[];

    if (current.includes(value)) {
      // Remove
      newValues = current.filter((v) => v !== value);
    } else {
      // Add
      newValues = [...current, value];
    }

    this.selectedValues.set(newValues);
    this.onChangeCallback(newValues);
    this.onTouchedCallback();
  }

  // ControlValueAccessor implementation
  writeValue(value: string[] | null): void {
    this.selectedValues.set(value ?? []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Can be implemented if needed for disabled state
  }
}
